import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedItem } from '../../components/PerinHealthGridSlice';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import FilterManagementView from '../../components/FilterManagementView';
import managementStyles from '../ManagementPages.module.css';
import styles from './HubManagement.module.css';
import {  OnlineIcon, OfflineIcon } from '../../components/icons/Icons';
import PatientHubInfoPopup from './components/PatientHubInfoPopup';
import AssignHubToPatientPopup from './components/AssignHubToPatientPopup';
import useAxios from '../../services/AxiosConfig'; // Import useAxios
import { useLocation } from 'react-router-dom';
import { formatDateString } from '../../utils/dateUtils';
import TabNavigation from '../../components/TabNavigation';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import PerinHealthModal from '../../components/PerinHealthModel';
import { openModal, closeModal } from '../../components/PerinHealthModelSlice';

const PatientHubManagement = () => {
    const location = useLocation();
    const groupName = location.state?.groupName || 'Default Group';
    const groupId = location.state?.groupId;
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const accountId = useSelector((state) => state.userInfo.id);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const [showPopup, setShowPopup] = useState(false);
    const [popupData, setPopupData] = useState({ items: [] });
    const [rawHubs, setRawHubs] = useState([]);
    const [groupMeta, setGroupMeta] = useState({ groupName: '', quantity: 0, numAssigned: 0 });
    const [patientNameMap, setPatientNameMap] = useState({});
    const [patientDobMap, setPatientDobMap] = useState({});
    const [showAssignPopup, setShowAssignPopup] = useState(false);
    const [selectedHub, setSelectedHub] = useState(null);
    const [patientOptions, setPatientOptions] = useState([]);

    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);

    const [activeTab, setActiveTab] = useState('assigned');

    const iconMapping = {
        'Online': { icon: <OnlineIcon />, text: 'Online' },
        'Offline': { icon: <OfflineIcon />, text: 'Offline' },
      };

    const headers = activeTab === 'assigned' ? [
        { key: 'id', label: "ID" },
        { key: 'assignedTo', label: "Assigned to"},
        { key: 'dob', label: "DOB"},
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "MAC Address"},
        { key: 'status', label: "Status"},
        { key: 'dateAssigned', label: "Date Assigned"},
    ] : [
        { key: 'id', label: "ID" },
        { key: 'serialNumber', label: "Serial Number"},
        { key: 'macAddress', label: "MAC Address"},
        { key: 'status', label: "Status"},
        { key: 'lastAssigned', label: "Last Assigned"},
    ];

    useEffect(() => {
        dispatch(setSelectedItem({}));
        getHubs(); // Fetch hubs data on component mount
        getPatients();
    }, [userDateFormat]);

    // Patients in this group that a hub can be assigned to, plus a lookup map
    // for resolving assigned hubs' patientId to a display name.
    const getPatients = async () => {
        try {
            const response = await httpService.get(`enrollment/list/patient/${accountId}`);
            if (Array.isArray(response.data.data)) {
                const options = [];
                const nameMap = {};
                const dobMap = {};
                response.data.data.forEach((patient) => {
                    nameMap[patient.id] = `${patient.firstName} ${patient.lastName}`;
                    dobMap[patient.id] = patient.healthInfo?.dateOfBirth || null;
                    if (patient.activelyEnrolledFlg) {
                        options.push({ id: patient.id, name: `${patient.firstName} ${patient.lastName}` });
                    }
                });
                setPatientOptions(options);
                setPatientNameMap(nameMap);
                setPatientDobMap(dobMap);
            }
        } catch (err) {
            console.error('Error fetching patients:', err);
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) {
            return "N/A"; // Return "N/A" if dateString is null or undefined
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return "N/A"; // Check if the date object is valid
        }
        return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
    };
    

    function formatTime(dateTimeString) {
        if (!dateTimeString) {
            return "N/A"; // Return "N/A" if dateTimeString is null or undefined
        }
        const time = new Date(dateTimeString);
        if (isNaN(time.getTime())) {
            return "N/A"; // Check if the datetime object is valid
        }
        return time.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };    

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return "N/A";
        const dateTime = new Date(dateTimeString);
        if (isNaN(dateTime.getTime())) return "N/A";
        const date = formatDateString(dateTime, userDateFormat);
        const time = formatTime(dateTime);
        return `${date} ${time}`;
    };
    

    const getHubs = async () => {    
        httpService.get(`hub/group/${accountId}`)
            .then((responseData) => {
                if (Array.isArray(responseData.data.data)) {
                    const groupData = responseData.data.data.find(hub => hub.groupId === groupId);

                    if (groupData) {
                        setRawHubs(groupData.hubs || []);
                        setGroupMeta({
                            groupName: groupData.groupName,
                            quantity: groupData.quantity || 0,
                            numAssigned: groupData.numAssigned || 0
                        });
                    } else {
                        console.error('Group data not found');
                    }
                } else {
                    console.error('Invalid data format');
                }
            })
            .catch((err) => {
                console.error('Error fetching hubs:', err);
            });
    };    

    // Assigned vs unassigned are both derived from the single `hubs` array
    // returned by the API, split on the `isAssigned` flag.
    const assignedHubData = useMemo(() => (
        rawHubs
            .filter(hub => hub.isAssigned)
            .map(hub => ({
                id: hub.id,
                patientId: hub.patientId,
                assignedTo: patientNameMap[hub.patientId] || (hub.patientId ? `Patient ${hub.patientId}` : "-"),
                dob: patientDobMap[hub.patientId] ? formatDateString(patientDobMap[hub.patientId], userDateFormat) : "N/A",
                serialNumber: hub.serialNumber,
                macAddress: hub.macAddress || "N/A",
                status: hub.status || 'Offline',
                groupName: groupMeta.groupName,
                dateAssigned: hub.dateAssigned ? formatDateString(hub.dateAssigned, userDateFormat) : "N/A",
                lastTransmissionDate: hub.lastTransmission ? formatDateString(hub.lastTransmission, userDateFormat) : "N/A",
                lastTransmissionTime: formatTime(hub.lastTransmission),
                lastConnection: formatDateTime(hub.lastConnection),
                eID: hub.eID
            }))
    ), [rawHubs, patientNameMap, patientDobMap, groupMeta.groupName, userDateFormat]);

    const unassignedHubData = useMemo(() => (
        rawHubs
            .filter(hub => !hub.isAssigned)
            .map(hub => ({
                id: hub.id,
                serialNumber: hub.serialNumber,
                macAddress: hub.macAddress || "N/A",
                status: hub.status || 'Offline',
                groupName: groupMeta.groupName,
                lastAssigned: hub.dateAssigned ? formatDateString(hub.dateAssigned, userDateFormat) : "N/A"
            }))
    ), [rawHubs, groupMeta.groupName, userDateFormat]);

    const totalHubs = groupMeta.quantity;
    const assignedHubs = groupMeta.numAssigned;

    const onRowClickHandler = (rowItem) => {
        const defaultData = {
            assignedTo: '',
            serialNumber: '',
            allStatuses: [], // Ensure this is always an array
            lastTransmissionDate: '',
            lastTransmissionTime: '',
            lastConnection: '',
            providerName: '',
            eID: ''
        };
    
        const rowData = {
            ...defaultData,
            ...rowItem
        };
    
        setPopupData(rowData);
        setShowPopup(true);
    };
       
     
    const onDeleteHandler = () => {
        // Define your delete logic here
    };

    const closePopup = () => {
        setShowPopup(false);
    };

    const handleTabSwitch = (tabName) => {
        setActiveTab(tabName);
    };

    const displayedHubs = activeTab === 'assigned' ? assignedHubData : unassignedHubData;

    // Patients available for assignment exclude anyone already attached to a hub.
    const availablePatientOptions = useMemo(() => {
        const assignedPatientIds = new Set(
            rawHubs.filter(hub => hub.isAssigned && hub.patientId != null).map(hub => hub.patientId)
        );
        return patientOptions.filter(patient => !assignedPatientIds.has(patient.id));
    }, [patientOptions, rawHubs]);

    const getDropdownOptions = () => (
        activeTab === 'unassigned'
            ? [{ label: "Assign Hub", type: "assign" }]
            : [{ label: "View Details", type: "view" }, { label: "Unassign Hub", type: "unassign" }]
    );

    const handleHubDropdownClick = ({ type, item }) => {
        if (type === 'assign') {
            setSelectedHub(item);
            setShowAssignPopup(true);
        } else if (type === 'view') {
            onRowClickHandler(item);
        } else if (type === 'unassign') {
            setSelectedHub(item);
            dispatch(openModal({
                activeModalId: "UnassignHub",
                header: "Unassign Hub",
                message: "Are you sure you want to unassign this hub?",
                buttons: [
                    { label: 'Cancel', variant: 'secondary', onClickKey: 'NO' },
                    { label: 'Unassign', variant: 'primary', onClickKey: 'YES' },
                ],
            }));
        }
    };

    const handleUnassignModalClick = (onClickKey) => {
        if (onClickKey === 'YES') {
            unassignHubFromPatient();
        }
        dispatch(closeModal());
    };

    const closeAssignPopup = () => {
        setShowAssignPopup(false);
        setSelectedHub(null);
    };

    const assignHubToPatient = async (patientId) => {
        try {
            const payload = {
                patientId: Number(patientId),
                hubRegistryId: selectedHub?.id
            };
            await httpService.post('hub/group/assign', payload);
            dispatch(showAlert({
                header: "Success",
                message: "Hub assigned successfully",
                type: 'success'
            }));
            closeAssignPopup();
            getHubs();
        } catch (error) {
            console.error('Error assigning hub:', error);
            dispatch(showAlert({
                header: "Error",
                message: "Failed to assign hub",
                type: 'danger'
            }));
        }
    };

    const unassignHubFromPatient = async () => {
        try {
            await httpService.post(`hub/group/unassign/${selectedHub?.id}`);
            dispatch(showAlert({
                header: "Success",
                message: "Hub unassigned successfully",
                type: 'success'
            }));
            closeAssignPopup();
            getHubs();
        } catch (error) {
            console.error('Error assigning hub:', error);
            dispatch(showAlert({
                header: "Error",
                message: "Failed to unassign hub",
                type: 'danger'
            }));
        }
    };

    return (
        <div className={managementStyles.MiddleContent}>
            <div style={{display:"flex", alignItems:"center", columnGap:"5px"}}>
                <span className="subtitle-1">{`${groupName} |`}</span>
                <span className={styles.HubsNumber}>{assignedHubs}</span>
                <span className={styles.HubsLabel}>/{totalHubs}</span>
                <span className={styles.HubsLabel} style={{color:"#30584A"}}>Hubs Assigned</span>
            </div>
            <TabNavigation
                    tabs={[
                        { key: 'assigned', label: 'Assigned' },
                        { key: 'unassigned', label: 'Unassigned' },
                    ]}
                    activeTab={activeTab}
                    onTabChange={handleTabSwitch}
                    justifyContent="space-between"
                    containerStyle={{ marginTop: '20px' }}
                />
            <FilterManagementView
                isOrganization={false}
                isGroup={false}
                showAddButton={false}
                showDeleteButton={false}
                onDeleteHandler={onDeleteHandler}
                modelHeader='Delete Hub?' 
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={displayedHubs}
                    headers={headers}
                    iconMapping={iconMapping} 
                    onDeleteRow={onDeleteHandler} 
                    showCheckbox={activeTab === 'assigned'}  
                    onRowValueClick={onRowClickHandler}
                    rowValueAction = {'assignedTo'}
                    gridHeight='100%'
                    showHeaderFilter={activeTab === 'assigned'}
                    showProfilePicture={activeTab === 'assigned' ? "assignedTo" : false}
                    showEditIcon={true}
                    moreEditOptions={getDropdownOptions}
                    onRowClick={handleHubDropdownClick}
                />
            </div>
            {showPopup && 
            <PatientHubInfoPopup data={popupData} onClose={closePopup} />
        }
            {showAssignPopup &&
            <AssignHubToPatientPopup
                hub={selectedHub}
                patients={availablePatientOptions}
                onClose={closeAssignPopup}
                onAssign={assignHubToPatient}
            />
        }
            <PerinHealthModal
                modalId={"UnassignHub"}
                handleButtonClick={handleUnassignModalClick}
            />
        </div>
    )
}

export default PatientHubManagement;
